import type { UserStudies, UserStudySite } from 'common/graphql/types';
import { useDispatch, useSelector } from 'react-redux';
import { AuthActions } from '../actions';
import { fillUserRecord } from '../helpers';
import { switchUserRoleDocumentNode } from '../queries';
import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import type { RootState } from 'store';
import { CIPageStatus } from 'common/viewcomp/general/ci_pagestatus';
import { useTranslation } from 'react-i18next';
import { CILogger } from 'common/log/log.provider';
import { featureFlags } from 'common/featureFlags/featureFlags';

export const useUpdateActiveStudySiteWithRole = () => {
    const activeUser = useSelector((state: RootState) => state.auth.activeUser);
    const userRoleSwitchPending = useSelector((state: RootState) => state.auth.userRoleSwitchPending);

    const dispatch = useDispatch();
    const [switchUserRoleMutation] = useMutation(switchUserRoleDocumentNode);
    const { t } = useTranslation('global');

    const updateActiveStudySite = useCallback(
        async ({ study, site }: { study: UserStudies; site: UserStudySite }) => {
            if (!featureFlags.fxIntegration || activeUser.authenticationMethod === 'COGNITO') {
                dispatch(
                    AuthActions.updateActiveStudySite({
                        site,
                        study,
                    })
                );

                return;
            }

            try {
                dispatch(AuthActions.updateUserRoleSwitchPending(true));
                await switchUserRoleMutation({
                    variables: {
                        userId: activeUser.pk,
                        subscriberId: activeUser.subscriberPk,
                        roleId: study.roleVClaim.sk,
                        fxRoleId: study.roleVClaim.fxRoleId,
                    },
                });

                const updatedUser = await fillUserRecord(activeUser);
                dispatch(
                    AuthActions.updateActiveStudySite({
                        site,
                        study,
                        updatedUser,
                    })
                );
                dispatch(AuthActions.updateUserRoleSwitchPending(false));
            } catch (e) {
                CIPageStatus.error(t('switch_role.errors.updating_active_study_site'));
                CILogger.getInstance().error(t('switch_role.errors.updating_active_study_site'), e);

                // TODO: might be a better idea to just invalidate the auth user object and let reload the data on next page instead
                dispatch(AuthActions.logoutRequest());
            }
        },
        [switchUserRoleMutation, dispatch, activeUser]
    );

    return [updateActiveStudySite, { loading: userRoleSwitchPending }] as const;
};
